import { RetailLocatorStoreCount } from "@client/apps/retail-locator/RetailLocatorStoreCount";
import React, { memo, useEffect, useId, useRef, useState } from "react";
import { BasketMenuIcon as BasketMenuIconComponent } from "tsi-common-react/src/apps/checkout/BasketMenuIcon/index";
import { BasketLineVariant } from "tsi-common-react/src/apps/checkout/constants";
import { AccountMenu } from "tsi-common-react/src/apps/customer-account/AccountMenu/index";
import { Link } from "tsi-common-react/src/components/Link/index";
import { RichNavigation } from "tsi-common-react/src/components/RichNavigation/index";
import { ValueProps } from "tsi-common-react/src/components/RichNavigationLinkCollection/index";
import { SVGIcon } from "tsi-common-react/src/components/SVGIcon/index";
import {
    WebPageURL,
    isoWebPageURL,
} from "tsi-common-react/src/models/nominals";
import { useCurrentBreakpoint } from "tsi-common-react/src/utils/hooks";
import {
    isPageInsideIFrame as isPageInsideIFrameCheck,
    usePageSetting,
} from "tsi-common-react/src/utils/settings";
import { useIsSSR } from "tsi-common-react/src/utils/ssr";
import { usePageURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { Context, reverse } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";
import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";
import {
    classNames,
    concatClassNames,
} from "@thelabnyc/thelabui/src/utils/styles";

import { FinancingModalTriggerMinicart } from "../../apps/checkout/FinancingModalTriggerMinicart";
import { DesktopRichNav } from "../DesktopRichNav";

import styles from "./index.module.scss";

// Props come from the global `chrome` context processor's `site_header` field
// (stearns `chrome.SiteHeaderProps`), the same shape PageSkeleton spreads in.
type ISiteHeaderProps = React.ContextType<
    typeof Context
>["chrome"]["site_header"];
type IRichNavigation = NonNullable<ISiteHeaderProps["rich_navigation"]>;
type INavList = NonNullable<ISiteHeaderProps["nav_list"]>;
type INavFeatures = NonNullable<
    ISiteHeaderProps["nav_feature"]
>["nav_features"];

type BreakpointState = {
    [query: string]: boolean;
};

interface IMenu {
    rich_navigation: IRichNavigation | null;
    // Desktop-only snippet; rendered inside the slide-out, replacing the
    // legacy nav content there when configured.
    desktop_rich_navigation: IRichNavigation | null;
    // nav_features / nav_list resolve to null/undefined when the header
    // settings don't name exactly one matching snippet (see chrome.py
    // SiteHeaderProps); the Menu body guards both before rendering.
    nav_features: INavFeatures | undefined;
    nav_list: INavList | null;
    viewport: BreakpointState;
    menuRef: React.RefObject<HTMLElement | null>;
    isMenuOpen: boolean;
}

interface IBasketMenuIconProps {
    basketURL: WebPageURL;
    checkoutURL: WebPageURL;
    isPageInsideIFrame: boolean;
    enableBasketPrequalSDK: boolean;
    isSSR: boolean;
}

const NavFeature = memo(({ nav_features }: { nav_features: INavFeatures }) => {
    return (
        <nav className="feature">
            {nav_features.value?.map((block, i) => (
                <React.Fragment key={`key-${i}`}>
                    {block?.value?.map((feature, index) => {
                        return (
                            <a href={feature?.page?.url} key={`key-${index}`}>
                                {feature.image && (
                                    <ImageChooserBlock
                                        attrs={{ className: styles.featureImg }}
                                        value={feature.image}
                                    />
                                )}
                                {feature.badge && (
                                    <div
                                        className={`feature-badge ${feature.badge?.position ? `feature-badge--${feature.badge?.position}` : ""}`}
                                    >
                                        <div className="feature-badge__image-container">
                                            {feature.badge.image?.url && (
                                                <img
                                                    src={
                                                        feature.badge.image.url
                                                    }
                                                    // TODO(i18n): Wrap alt text in t tagged template: alt={t`Feature Badge Image`}
                                                    alt="Feature Badge Image"
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}
                                <span>
                                    {feature.label}
                                    <SVGIcon
                                        name="arrowRight"
                                        alt={t`arrow right icon`}
                                    />
                                </span>
                            </a>
                        );
                    })}
                </React.Fragment>
            ))}
        </nav>
    );
});

const NavList = memo(({ nav_list }: { nav_list: INavList }) => {
    // Accordion toggle owned by this component's own state. Defaults open to
    // match the prior static markup (`--is-active` / `aria-expanded="true"`).
    const [isOpen, setIsOpen] = useState(true);
    const targetId = useId();
    return (
        <nav className="nav-list">
            {!nav_list.hide_heading && (
                <h5>
                    <button
                        className={`accordion-trigger${
                            isOpen ? " --is-active" : ""
                        }`}
                        aria-controls={targetId}
                        aria-expanded={isOpen ? "true" : "false"}
                        onClick={(event) => {
                            event.preventDefault();
                            setIsOpen((value) => !value);
                        }}
                    >
                        {nav_list.heading}
                    </button>
                </h5>
            )}

            <div
                className={`accordion-target${
                    isOpen ? " accordion-target--is-active" : ""
                }`}
                id={targetId}
                aria-hidden={isOpen ? "false" : "true"}
            >
                {nav_list.body.value &&
                    nav_list.body.value.map((item) => {
                        if (item.type !== "html") {
                            if (!item.value) {
                                return null;
                            }
                            // Check all Link types for page and integrate page.url
                            // Otherwise look for external_url of LinkBlock
                            const url = item.value.page
                                ? item.value.page.url
                                : item.type === "link" &&
                                  item.value.external_url;
                            return (
                                <a
                                    href={url || "/"}
                                    target="_top"
                                    key={item.id}
                                >
                                    {item.value.display_name}
                                </a>
                            );
                        }
                        return (
                            <div
                                key={item.id}
                                dangerouslySetInnerHTML={{ __html: item.value }}
                            />
                        );
                    })}
            </div>
        </nav>
    );
});

const MenuTrigger = ({
    isMenuOpen,
    handleMenuOpen,
}: {
    isMenuOpen: boolean;
    handleMenuOpen: (isOpen: boolean) => void;
}) => {
    return (
        <>
            {!isMenuOpen && (
                <button
                    type="button"
                    className={styles.menuLink}
                    aria-controls="menu"
                    aria-expanded={isMenuOpen}
                    onClick={() => handleMenuOpen(true)}
                >
                    <SVGIcon
                        className={styles.headerSvg}
                        name="hamburgerIcon"
                        alt={t`Hamburger navigation icon`}
                    />
                    <span className={styles.triggerLabel}>{t`Menu`}</span>
                </button>
            )}
            {isMenuOpen && (
                <button
                    type="button"
                    className={styles.closeLink}
                    aria-controls="menu"
                    aria-expanded={isMenuOpen}
                    onClick={() => handleMenuOpen(false)}
                >
                    <SVGIcon
                        className={styles.iconContainer}
                        svgClassName={styles.headerSvg}
                        name="closeIcon"
                        alt={t`Menu close icon`}
                    />
                    <span className={styles.triggerLabel}>{t`Close`}</span>
                </button>
            )}
        </>
    );
};

const Menu = memo(
    ({
        rich_navigation,
        desktop_rich_navigation,
        nav_features,
        nav_list,
        viewport,
        menuRef,
        isMenuOpen,
    }: IMenu) => {
        const [shouldRender, setShouldRender] = useState(false);

        useEffect(() => {
            if (isMenuOpen) {
                // As soon as we get a true value, ensure the menu is in the DOM
                setShouldRender(true);
            }

            // Close the menu safely in no animation environments
            if (!isMenuOpen && !viewport.belowMobile) {
                setShouldRender(false);
            }
        }, [isMenuOpen]);

        const onAnimationEnd = () => {
            if (!isMenuOpen) {
                // Once the closing animation ends, remove the element from the DOM
                setShouldRender(false);
            }
        };

        // Desktop: a configured desktop snippet replaces the legacy nav content
        // inside the slide-out. Mobile is unchanged.
        const desktopRichNav = !viewport.belowMobile
            ? desktop_rich_navigation
            : null;

        const menuClasses = classNames([
            ["menu", true],
            [styles.menu, true],
            [styles.richNavigationActive, !!rich_navigation],
            [styles.opening, isMenuOpen],
            [styles.closing, !isMenuOpen],
        ]);

        return (
            <>
                {shouldRender && (
                    <section
                        ref={menuRef}
                        className={menuClasses}
                        // TODO(i18n): Wrap aria-label in t tagged template: aria-label={t`Site Menu`}
                        aria-label="Site Menu"
                        role="dialog"
                        aria-modal="true"
                        onAnimationEnd={onAnimationEnd}
                    >
                        {desktopRichNav ? (
                            <DesktopRichNav
                                desktop_rich_navigation={desktopRichNav}
                            />
                        ) : (
                            <>
                                {nav_features && (
                                    <NavFeature nav_features={nav_features} />
                                )}

                                {nav_list && <NavList nav_list={nav_list} />}

                                {rich_navigation && viewport.belowMobile && (
                                    <div
                                        id="mobile-panel"
                                        className="rich-navigation"
                                    >
                                        <RichNavigation
                                            openMobileContentDefaultState={true}
                                            {...rich_navigation}
                                        />
                                        <ValueProps
                                            value_props={
                                                rich_navigation.value_props
                                            }
                                            enableValuePropsOnMobile
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </section>
                )}
            </>
        );
    },
);

const BasketMenuIcon = memo(
    ({
        basketURL,
        checkoutURL,
        isPageInsideIFrame,
        enableBasketPrequalSDK,
        isSSR,
    }: IBasketMenuIconProps) => (
        <BasketMenuIconComponent
            basketURL={basketURL}
            checkoutURL={checkoutURL}
            isPageInsideIFrame={isPageInsideIFrame}
            basketLineVariant={BasketLineVariant.MINIMAL_ENHANCED}
            buildPromoComponents={(closeModal) => {
                if (!enableBasketPrequalSDK) {
                    return null;
                }
                return (
                    <FinancingModalTriggerMinicart closeModal={closeModal} />
                );
            }}
            isSSR={isSSR}
        />
    ),
);

export const SiteHeader = (props: ISiteHeaderProps) => {
    const isSSR = useIsSSR();
    const viewport = useCurrentBreakpoint();
    const menuRef = useRef<HTMLElement>(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { rich_navigation, nav_list, nav_feature, desktop_rich_navigation } =
        props;

    const homeURL = usePageURL("home");
    const basketURL = isoWebPageURL.wrap(reverse("basket:summary"));
    const checkoutURL = isoWebPageURL.wrap(reverse("checkout:index"));
    const findARetailerURL = usePageURL("find_a_retailer");
    const isPageInsideIFrame = isSSR ? false : isPageInsideIFrameCheck();
    const enableBasketPrequalSDK = usePageSetting("show_prequal_cart") || false;
    const enableEcom = usePageSetting("enable_ecom") ?? true;

    const basketMenuIconProps = {
        basketURL,
        checkoutURL,
        isPageInsideIFrame,
        enableBasketPrequalSDK,
        isSSR,
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const outsideClickEvent = e.target as Node;
            // If the clicked element is not inside the dropdown, close it
            if (
                menuRef.current &&
                !menuRef.current.contains(outsideClickEvent)
            ) {
                handleMenuOpen(false);
            }
        };
        const document = getDocument();
        if (!document) return;
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Including legacy EventListener code during the ongoing SiteHeader refactor
    const includeChangesAfterClickEvent = (isOpen: boolean) => {
        const zIndexValue = isOpen ? "200" : "";
        const document = getDocument();
        if (!document) return;
        const header = document.querySelector<HTMLElement>("body > header");
        const footer = document.querySelector<HTMLElement>("body > footer");
        if (header) {
            header.style.zIndex = zIndexValue;
        }
        if (footer && viewport.belowMobile) {
            footer.style.zIndex = zIndexValue;
        }
    };

    const lockBodyOnMenuOpen = (isOpen: boolean) => {
        const document = getDocument();
        if (!document || !document.body) return;
        if (isOpen && !isSSR) {
            // disable body
            document.body.classList.remove("overflow--initial");
            document.body.classList.add("overflow--y-hidden");
        } else {
            document.body.classList.add("overflow--initial");
            document.body.classList.remove("overflow--y-hidden");
        }
    };

    const handleMenuOpen = (isOpen: boolean) => {
        setIsMenuOpen(isOpen);
        includeChangesAfterClickEvent(isOpen);
        lockBodyOnMenuOpen(isOpen);
    };

    return (
        <header className={rich_navigation ? "rich-navigation__header" : ""}>
            <a className="screen-reader-skip" href="#main-content">
                {t`Skip to main content`}
            </a>
            <nav className="header-links">
                <MenuTrigger
                    isMenuOpen={isMenuOpen}
                    handleMenuOpen={handleMenuOpen}
                />

                <Menu
                    rich_navigation={rich_navigation}
                    desktop_rich_navigation={desktop_rich_navigation}
                    nav_features={nav_feature?.nav_features}
                    nav_list={nav_list}
                    viewport={viewport}
                    menuRef={menuRef}
                    isMenuOpen={isMenuOpen}
                />

                <Link
                    className="logo-link"
                    href={homeURL}
                    // TODO(i18n): Wrap title in t tagged template: title={t`Return to Stearns & Foster Home Page`}
                    title="Return to Stearns &amp; Foster Home Page"
                    target="_top"
                >
                    <SVGIcon
                        className={styles.iconContainer}
                        svgClassName={concatClassNames([
                            styles.logo,
                            styles.headerSvg,
                        ])}
                        name="stearnsLogo"
                        alt={t`Stearns &amp; Foster Logo`}
                    />
                </Link>
                <div className="header-icons">
                    <Link
                        className="retail-locator-link"
                        href={findARetailerURL}
                        // TODO(i18n): Wrap title in t tagged template: title={t`Find a Retailer`}
                        title="Find a Retailer"
                        target="_top"
                    >
                        <span className="ada-screenreader-only">
                            {t`Nearest retail locations`}
                        </span>
                        <SVGIcon
                            className={styles.iconContainer}
                            svgClassName={styles.headerSvg}
                            name="pinIcon"
                            alt={t`Pin icon`}
                        />
                        {!viewport.belowMobile && (
                            <RetailLocatorStoreCount type={"bare-count"} />
                        )}
                    </Link>

                    {enableEcom && (
                        <>
                            <AccountMenu />
                            <BasketMenuIcon {...basketMenuIconProps} />
                        </>
                    )}
                </div>
            </nav>
        </header>
    );
};
